import { Grid } from "@mui/material";
import DialogComponent from "../../../../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../../../../common/components/functional/dialogs/DialogContentWrapper";
import { useTranslation } from "react-i18next";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { UnsavedChangesProvider } from "../../../../../contexts/UnsavedChangesContext";
import { SimpleTable } from "../../../../../common/components/functional/simpleTable/SimpleTable";
import { SettingsHeader } from "./SettingsHeader";
import { SettingsItem } from "./SettingsItem";
import {
  EditWorkTimeBankSettings,
  FormattedWorkTimeBankSetting,
  CommonWorkTimeBankSettingMode,
} from "../../../../employment/employmentApi";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import {
  updateEmploymentFlexibleHoursSettings,
  updateEmploymentWorkTimeBankSettings,
} from "../../../../employment/employmentSlice";
import { isFulfilled } from "@reduxjs/toolkit";
import { useContext, useEffect, useMemo } from "react";
import { addDays } from "date-fns";
import isEmpty from "../../../../../common/helpers/isEmpty";
import useIsLoading from "../../../../../hooks/useIsLoading";
import { AccessRightContext } from "../../../../user/AccessRightProvider";
import { constructPayload } from "./helpers";

type Props = {
  workTimeBankSettings: FormattedWorkTimeBankSetting[] | undefined;
  isOpen: boolean;
  onClose: () => void;
  type: "workTimeBank" | "flexiHours";
};

export type FormValues = {
  workTimeBankSettings: FormattedWorkTimeBankSetting[];
};

export const SettingsDialog = ({
  workTimeBankSettings,
  isOpen,
  onClose,
  type,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("talentProfile");
  const isLoading = useIsLoading(["employment"]);

  const employmentAdditionalInfo = useAppSelector(
    (state) => state.employment.additionalInfo,
  );
  const { canEditEmployment } = useContext(AccessRightContext);
  const canEdit = canEditEmployment(employmentAdditionalInfo?.employerId);

  const sortedWorkTimeBankSettings = useMemo(() => {
    if (!workTimeBankSettings) return [];
    return [...workTimeBankSettings].sort((a, b) => {
      return a.from.getTime() - b.from.getTime();
    });
  }, [workTimeBankSettings]);

  const defaultValues: FormValues = useMemo(
    () => ({
      workTimeBankSettings: sortedWorkTimeBankSettings || [],
    }),
    [sortedWorkTimeBankSettings],
  );

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  // Reset form after work time bank settings change in employment
  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const {
    control,
    getValues,
    formState: { isDirty, errors },
  } = methods;

  const {
    fields: settingsFields,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: "workTimeBankSettings",
  });

  const sortList = () => {
    const list = getValues().workTimeBankSettings;
    const sortedList = [...list].sort((a, b) => {
      const dateA = new Date(a.from);
      const dateB = new Date(b.from);
      return dateA.getTime() - dateB.getTime();
    });
    replace(sortedList);
  };

  const saveDisabled = !isDirty || !isEmpty(errors) || isLoading || !canEdit;

  const close = () => {
    methods.reset();
    onClose();
  };

  const save = (values: FormValues) => {
    const payload: EditWorkTimeBankSettings = constructPayload(
      values,
      employmentAdditionalInfo,
    );
    if (type === "workTimeBank") {
      dispatch(updateEmploymentWorkTimeBankSettings(payload)).then((action) => {
        if (isFulfilled(action)) {
          close();
        }
      });
    } else {
      dispatch(updateEmploymentFlexibleHoursSettings(payload)).then(
        (action) => {
          if (isFulfilled(action)) {
            close();
          }
        },
      );
    }
  };

  const onAddItem = () => {
    const previousItem = settingsFields[settingsFields.length - 1];
    const suggestedStartDate = previousItem?.to
      ? addDays(previousItem?.to, 1)
      : previousItem?.from
        ? addDays(previousItem?.from, 1)
        : new Date(employmentAdditionalInfo.details.agreedStart);
    append({
      from: suggestedStartDate,
      to: null,
      mode: CommonWorkTimeBankSettingMode.IMMEDIATE,
      min: "",
      max: "",
    });
  };

  const onRemoveItem = (index: number) => {
    remove(index);
  };

  return (
    <DialogComponent open={isOpen} sx={{ zIndex: 1 }}>
      <DialogContentWrapper
        margin
        title={
          type === "workTimeBank"
            ? t("workTimeBankEditTitle")
            : t("flexiHoursEditTitle")
        }
        onCancel={close}
        onSave={methods.handleSubmit(save)}
        saveDisabled={saveDisabled}
      >
        <FormProvider {...methods}>
          <Grid
            container
            item
            xs={12}
            sx={{
              maxHeight: "60vh",
              overflow: "scroll",
              scrollbarWidth: "none",
            }}
          >
            <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
              <SimpleTable
                header={<SettingsHeader />}
                items={settingsFields.map((settingRow, index) => (
                  <SettingsItem
                    id={settingRow.id}
                    index={index}
                    removeItem={onRemoveItem}
                    sortList={sortList}
                    canEdit={canEdit}
                    type={type}
                  />
                ))}
                addItem={onAddItem}
                canAdd={canEdit}
                addButtonVariant="text"
              />
            </UnsavedChangesProvider>
          </Grid>
        </FormProvider>
      </DialogContentWrapper>
    </DialogComponent>
  );
};
